import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Menu, MenuItem,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsIcon from '@mui/icons-material/Settings';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getCurrentUserId } from '../authUtils';
import { useRolePermissions } from '../context/RolePermissionsContext';
import {
  createFileRecord, getAdditionalFileUploadSignedUrl, getFinancialFileUploadSignedUrl,
  startUnderwritingFlow,
} from '../api/companyApi';
import {
  createAdditionalFile, generateCompanyReport, listCompanySummaryData, processFile, updateCompanySummary,
} from '../api/underwritingApi';
import { createQuickTask, deleteQuickTask, listQuickTasks, updateQuickTask } from '../api/quickTasksApi';
import { logApproval, updateTenant } from '../api/tenantApi';
import { isExecutiveDashboardEnabled } from '../config/tenantRollouts';

const FILTERS = [
  ['all', 'All tasks'], ['new', 'New companies'], ['ai', 'AI Recommended'],
  ['risk', 'Riskiest companies'], ['transferred', 'Tasks transferred to you'],
];
const FILE_TYPES = [
  ['financial', 'Financial statements'], ['credit', 'Credit report'], ['supporting', 'Supporting document'],
];
const FILTER_HELP = {
  all: 'Every open task currently assigned or recommended to you.',
  new: 'Recently added companies that are most likely to need an initial review.',
  ai: 'Companies where the AI recommends a new or refreshed underwriting review.',
  risk: 'Companies with the highest ARC risk scores, ordered by latest activity.',
  transferred: 'Open tasks that another team member assigned directly to you.',
};
const HEADER_HELP = {
  Counterparty: 'Open the full company report for deeper review.',
  'Why it needs attention': 'The signal or teammate request that created this task.',
  'Requested limit': 'The credit limit requested for this counterparty.',
  'Recommended limit': 'The limit recommended by the underwriting analysis.',
  'Approved limit': 'The credit limit currently approved by your team.',
  'ARC score': 'CrediArc risk score; higher values indicate higher risk.',
  'Status / progress': 'Live progress for uploads, underwriting, exports, and transferred work.',
  'Next action': 'Role-specific actions you are most likely to take next.',
  '': 'Mark a finished task complete and remove it from the open list.',
};
export const NEW_COMPANY_DAYS = 7;
const CLOSED_STATUSES = new Set(['completed', 'closed', 'done']);

const asDate = (value) => {
  if (!value) return null;
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const nameOf = (company) => company?.['Company Name'] || company?.companyName || company?.name || 'Unnamed company';
const scoreOf = (company) => Number(company?.['Combined Score'] ?? company?.combinedScore ?? company?.arcScore);
const requestedLimitOf = (company) => Number(company?.requestedCreditLimit ?? company?.['Requested Limit']);
const recommendedLimitOf = (company) => Number(company?.['Recommended Limit'] ?? company?.recommendedLimit);
const approvedLimitOf = (company) => Number(company?.approvedLimit ?? company?.['Approved Limit']);
const sourceKey = (companyId, category) => `${companyId}:${category}`;
const money = (value) => Number.isFinite(value) && value !== 0
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  : '—';
const score = (value) => Number.isFinite(value) ? value.toFixed(1) : '—';
const taskTime = (task) => asDate(task.updatedAt || task.createdAt || task.company?.createdAt)?.getTime() || 0;
export const companyReportPath = (tenantId, companyId, adminAction = false) => adminAction
  ? `/tenant-dashboard/${tenantId}/company/${companyId}`
  : `/company/${companyId}`;
export const buildDecisionReturnTask = (task, { decision, reason, action }) => ({
  companyId: task.companyId,
  companyName: task.companyName,
  assigneeUserId: task.assignedByUserId,
  assigneeName: task.assignedByName,
  taskType: 'approval_result',
  parentTaskId: task.id,
  action,
  reason: `VP Risk ${decision}${reason ? `: ${reason}` : ''}`,
});

export function deriveQuickTasks(companies = [], now = Date.now()) {
  const cutoff = now - NEW_COMPANY_DAYS * 24 * 60 * 60 * 1000;
  const tasks = [];
  companies.forEach((company) => {
    if (!company?.companyId) return;
    const created = asDate(company.createdAt);
    const hasSummaryData = company.hasSummaryData === true || company.summaryData != null;
    const score = scoreOf(company);
    const add = (category, reason, priority) => tasks.push({
      id: sourceKey(company.companyId, category), sourceKey: sourceKey(company.companyId, category),
      companyId: company.companyId, companyName: nameOf(company), category, reason, priority,
      // A bulk upload creates the company before its first underwriting summary exists.
      // Keep that company visible rather than hiding it until underwriting completes.
      status: company.underwritingInProgress
        ? 'Underwriting in progress'
        : hasSummaryData ? 'Ready' : 'Underwriting queued',
      company,
    });
    if (created && created.getTime() >= cutoff) add('new', 'Added in the last 7 days — initial underwriting is queued', 1);
    if (Number.isFinite(score) && score >= 8) add('risk', `High risk score (${score.toFixed(1)})`, 3);
    if (!company.lastUnderwritingDate || (Number.isFinite(score) && score >= 7)) {
      add('ai', !company.lastUnderwritingDate ? 'AI recommends running the first underwriting' : 'AI recommends a fresh review', 2);
    }
  });
  return tasks;
}

const reportBlob = (base64) => {
  const padded = String(base64 || '').padEnd(String(base64 || '').length + ((4 - String(base64 || '').length % 4) % 4), '=');
  const bytes = atob(padded);
  const array = new Uint8Array(bytes.length);
  for (let index = 0; index < bytes.length; index += 1) array[index] = bytes.charCodeAt(index);
  return new Blob([array], { type: 'application/pdf' });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ExplainedMenuItem = ({ explanation, disabled = false, children, onClick, ...props }) => (
  <Tooltip title={explanation} placement="right" arrow>
    <MenuItem
      {...props}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={disabled ? undefined : onClick}
      sx={{ width: '100%', ...(disabled ? { opacity: .45, cursor: 'not-allowed' } : {}) }}
    >
      {children}
    </MenuItem>
  </Tooltip>
);

export default function QuickTasksTable({ tenantId, companies = [], config = {}, adminAction = false, onConfigChanged, onCompaniesChanged }) {
  const executive = isExecutiveDashboardEnabled(tenantId);
  const { permissions, role } = useRolePermissions();
  const navigate = useNavigate();
  const userId = getCurrentUserId();
  const [filter, setFilter] = useState(config.defaultFilter || 'new');
  const [transferred, setTransferred] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [running, setRunning] = useState({});
  const [menu, setMenu] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, task: null, reason: '' });
  const [draftConfig, setDraftConfig] = useState({ maxRows: 5, defaultFilter: 'new' });
  const fileInput = useRef(null);
  const vpDefaultApplied = useRef(false);
  const isVpRisk = String(role?.name || '').toLowerCase() === 'vp risk';
  const roleName = String(role?.name || '').trim();
  const companyUrl = (companyId) => companyReportPath(tenantId, companyId, adminAction);

  useEffect(() => {
    setDraftConfig({ maxRows: 5, defaultFilter: 'new', ...config });
  }, [config]);

  useEffect(() => {
    if (isVpRisk && !vpDefaultApplied.current) {
      setFilter('transferred');
      vpDefaultApplied.current = true;
    }
  }, [isVpRisk]);

  useEffect(() => {
    if (!tenantId || !userId || !roleName) return;
    const key = `quick-tasks-intro:v3:${tenantId}:${userId}:${roleName.toLowerCase()}`;
    if (localStorage.getItem(key) !== 'seen') setIntroOpen(true);
  }, [tenantId, userId, roleName]);

  const closeIntro = () => {
    if (tenantId && userId && roleName) {
      localStorage.setItem(`quick-tasks-intro:v3:${tenantId}:${userId}:${roleName.toLowerCase()}`, 'seen');
    }
    setIntroOpen(false);
  };

  const loadTransferred = useCallback(async () => {
    if (!tenantId || !userId) return;
    try {
      const result = await listQuickTasks(tenantId, userId, { includeClosed: true });
      const tasks = result?.data || result || [];
      const serverDismissed = tasks
        .filter((task) => task.taskType === 'dismissal' && CLOSED_STATUSES.has(String(task.status || '').toLowerCase()))
        .map((task) => task.sourceKey)
        .filter(Boolean);
      setDismissed((current) => [...new Set([...current, ...serverDismissed])]);
      setTransferred(tasks.filter((task) => task.taskType !== 'dismissal' && !CLOSED_STATUSES.has(String(task.status || '').toLowerCase())));
    } catch (error) {
      console.error('Could not load transferred quick tasks', error);
    }
  }, [tenantId, userId]);

  useEffect(() => { loadTransferred(); }, [loadTransferred]);
  useEffect(() => {
    try { setDismissed(JSON.parse(localStorage.getItem(`quick-tasks-dismissed:${tenantId}:${userId}`) || '[]')); }
    catch (_) { setDismissed([]); }
  }, [tenantId, userId]);

  useEffect(() => {
    setMinimized(localStorage.getItem(`quick-tasks-minimized:v1:${tenantId}:${userId}`) === 'true');
  }, [tenantId, userId]);

  const derived = useMemo(() => deriveQuickTasks(companies).filter((task) => !dismissed.includes(task.sourceKey)), [companies, dismissed]);
  const companyMap = useMemo(() => new Map(companies.map((company) => [company.companyId, company])), [companies]);
  const transferredRows = useMemo(() => transferred.map((task) => ({
    ...task, id: task.id || task.taskId, category: 'transferred',
    company: companyMap.get(task.companyId),
    reason: task.reason || `Transferred by ${task.assignedByName || 'a team member'}`,
    status: task.label || (task.status === 'in_progress' ? 'In progress' : task.status === 'waiting_documents' ? 'Waiting for documents' : 'Assigned to you'),
  })), [transferred, companyMap]);
  const allRows = useMemo(() => [...transferredRows, ...derived], [transferredRows, derived]);
  const maxRows = Math.max(1, Math.min(50, Number(config.maxRows) || 5));
  const tableViewportHeight = 64 + maxRows * 56;
  const rows = useMemo(() => {
    const matchingRows = filter === 'all' ? allRows : allRows.filter((task) => task.category === filter);
    return [...matchingRows]
      .sort((left, right) => taskTime(right) - taskTime(left));
  }, [allRows, filter]);
  const count = (key) => key === 'all' ? allRows.length : allRows.filter((task) => task.category === key).length;
  const toggleMinimized = () => {
    const next = !minimized;
    setMinimized(next);
    setMenu(null);
    localStorage.setItem(`quick-tasks-minimized:v1:${tenantId}:${userId}`, String(next));
  };

  const setProgress = (task, patch) => setRunning((current) => ({ ...current, [task.id]: { ...current[task.id], ...patch } }));
  const persistProgress = async (task, patch) => {
    setProgress(task, patch);
    if (task.category === 'transferred' && task.id) {
      try { await updateQuickTask(tenantId, task.id, patch); } catch (_) { /* UI progress still remains visible */ }
    }
  };

  const finishTask = async (task) => {
    try {
      if (task.category === 'transferred') {
        await deleteQuickTask(tenantId, task.id);
        setTransferred((current) => current.filter((item) => (item.id || item.taskId) !== task.id));
      } else {
        await createQuickTask(tenantId, {
          companyId: task.companyId,
          companyName: task.companyName,
          assigneeUserId: userId,
          taskType: 'dismissal',
          category: task.category,
          sourceKey: task.sourceKey,
        });
        const next = [...new Set([...dismissed, task.sourceKey])];
        setDismissed(next);
        localStorage.setItem(`quick-tasks-dismissed:${tenantId}:${userId}`, JSON.stringify(next));
      }
      setRunning((current) => { const next = { ...current }; delete next[task.id]; return next; });
    } catch (error) { toast.error(error?.response?.data?.message || 'Could not finish this task'); }
  };

  const exportReport = async (task) => {
    await persistProgress(task, { action: 'Export report', status: 'in_progress', progress: 15, label: 'Generating report…' });
    const response = await generateCompanyReport(tenantId, task.companyId);
    setProgress(task, { progress: 85, label: 'Preparing download…' });
    const blob = reportBlob(response?.data || response);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${task.companyName || 'Company'}-Report.pdf`;
    document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    await persistProgress(task, { status: 'completed', progress: 100, label: 'Report downloaded' });
  };

  const waitForUnderwriting = async (task, startedAt) => {
    for (let attempt = 0; attempt < 120; attempt += 1) {
      await sleep(5000);
      const result = await listCompanySummaryData(tenantId);
      const company = (result?.data || []).find((item) => item.companyId === task.companyId);
      const completedAt = asDate(company?.underwritingTiming?.at || company?.updatedAt)?.getTime() || 0;
      const progress = Math.min(92, 15 + Math.round(attempt * 0.7));
      setProgress(task, { progress, label: company?.underwritingInProgress === false ? 'Finalizing…' : 'Underwriting in progress…' });
      if (company && company.underwritingInProgress === false && completedAt >= startedAt) return;
    }
    throw new Error('Underwriting is still running. The task will remain available.');
  };

  const refresh = async (task, exportWhenReady = false) => {
    const startedAt = Date.now();
    await persistProgress(task, { action: exportWhenReady ? 'Export after refresh' : 'Refresh underwriting', status: 'in_progress', progress: 5, label: 'Starting underwriting…' });
    await startUnderwritingFlow(tenantId, task.companyId);
    setProgress(task, { progress: 12, label: 'Underwriting queued…' });
    await waitForUnderwriting(task, startedAt);
    if (onCompaniesChanged) await onCompaniesChanged();
    if (exportWhenReady) await exportReport(task);
    else await persistProgress(task, { status: 'completed', progress: 100, label: 'Underwriting refreshed' });
  };

  const runAction = async (task, action) => {
    if (running[task.id]?.status === 'in_progress') return;
    try {
      if (action === 'refresh') await refresh(task, false);
      if (action === 'export') await exportReport(task);
      if (action === 'exportAfter') await refresh(task, true);
      toast.success(action === 'exportAfter' ? 'Underwriting finished and report downloaded' : 'Task action completed');
    } catch (error) {
      console.error('Quick task action failed', error);
      await persistProgress(task, { status: 'failed', label: error?.response?.data?.message || error.message || 'Action failed' });
      toast.error(error?.response?.data?.message || error.message || 'Action failed');
    }
  };

  const transferForApproval = async (task) => {
    try {
      setProgress(task, { status: 'in_progress', progress: 35, label: 'Transferring to VP Risk…' });
      const result = await createQuickTask(tenantId, {
        companyId: task.companyId, companyName: task.companyName,
        assigneeRoleName: 'VP Risk', taskType: 'approval', action: 'Approve or request documents',
        reason: `Approval requested for ${task.companyName}`,
      });
      const created = result?.data || result;
      setProgress(task, { status: 'completed', progress: 100, label: `Transferred to ${created?.assigneeName || 'VP Risk'}` });
      toast.success(`Approval transferred to ${created?.assigneeName || 'VP Risk'}`);
    } catch (error) {
      setProgress(task, { status: 'failed', progress: 0, label: error?.response?.data?.error || error.message || 'Transfer failed' });
      toast.error(error?.response?.data?.error || error.message || 'Could not transfer for approval');
    }
  };

  const returnDecisionToSender = async (task, { decision, reason, action }) => {
    if (!task.assignedByUserId) throw new Error('The original sender is unavailable for this task');
    return createQuickTask(tenantId, buildDecisionReturnTask(task, { decision, reason, action }));
  };

  const approveTask = async (task) => {
    if (!task.assignedByUserId) {
      toast.error('The original sender is unavailable; this approval was not changed.');
      return;
    }
    const approvedLimit = Number(
      task.company?.approvedLimit || task.company?.['Approved Limit'] ||
      task.company?.['Recommended Limit'] || task.company?.recommendedLimit ||
      task.company?.requestedCreditLimit || task.company?.['Requested Limit'] || 0
    );
    if (!approvedLimit) {
      toast.info('Set a requested, recommended, or approved limit before approving.');
      navigate(companyUrl(task.companyId));
      return;
    }
    try {
      await persistProgress(task, { action: 'Approve', status: 'in_progress', progress: 35, label: 'Recording approval…' });
      await updateCompanySummary(tenantId, task.companyId, { approvalStatus: 'APPROVED', approvedLimit });
      try { await logApproval(task.companyId, { approvedLimit, requestedLimit: task.company?.requestedCreditLimit, recommendedLimit: task.company?.['Recommended Limit'], arcScore: scoreOf(task.company) }); } catch (_) { /* approval itself succeeded */ }
      await returnDecisionToSender(task, {
        decision: `approved ${task.companyName}`,
        reason: `Approved limit ${money(approvedLimit)}`,
        action: 'Review VP Risk approval',
      });
      await updateQuickTask(tenantId, task.id, { status: 'completed', progress: 100, label: 'Approved' });
      setTransferred((current) => current.filter((item) => (item.id || item.taskId) !== task.id));
      if (onCompaniesChanged) await onCompaniesChanged();
      toast.success(`${task.companyName} approved and returned to ${task.assignedByName || 'the sender'}`);
    } catch (error) {
      await persistProgress(task, { status: 'failed', progress: 0, label: error?.response?.data?.message || error.message || 'Approval failed' });
      toast.error(error?.response?.data?.message || error.message || 'Approval failed');
    }
  };

  const rejectTask = async () => {
    const task = rejectDialog.task;
    const reason = rejectDialog.reason.trim();
    if (!task || !reason) return;
    if (!task.assignedByUserId) {
      setRejectDialog({ open: false, task: null, reason: '' });
      toast.error('The original sender is unavailable; this approval was not changed.');
      return;
    }
    setRejectDialog({ open: false, task: null, reason: '' });
    try {
      await persistProgress(task, { action: 'Reject', status: 'in_progress', progress: 35, label: 'Recording rejection…' });
      await updateCompanySummary(tenantId, task.companyId, { approvalStatus: 'REJECTED', approvalRejectionReason: reason });
      await returnDecisionToSender(task, {
        decision: `rejected ${task.companyName}`,
        reason,
        action: 'Review VP Risk rejection',
      });
      await updateQuickTask(tenantId, task.id, { status: 'completed', progress: 100, label: 'Rejected' });
      setTransferred((current) => current.filter((item) => (item.id || item.taskId) !== task.id));
      if (onCompaniesChanged) await onCompaniesChanged();
      toast.success(`Rejection returned to ${task.assignedByName || 'the sender'}`);
    } catch (error) {
      await persistProgress(task, { status: 'failed', progress: 0, label: error?.response?.data?.message || error.message || 'Rejection failed' });
      toast.error(error?.response?.data?.message || error.message || 'Could not reject this request');
    }
  };

  const requestDocuments = async (documentType) => {
    const task = menu?.task;
    setMenu(null);
    if (!task?.assignedByUserId) {
      toast.error('The original sender is unavailable for this task');
      return;
    }
    try {
      const result = await createQuickTask(tenantId, {
        companyId: task.companyId, companyName: task.companyName,
        assigneeUserId: task.assignedByUserId, assigneeName: task.assignedByName,
        taskType: 'document_request', parentTaskId: task.id, requestedDocumentType: documentType,
        action: 'Upload requested documents', reason: `VP Risk requested: ${documentType}`,
      });
      if (!(result?.data || result)) throw new Error('Document request was not created');
      await updateQuickTask(tenantId, task.id, { status: 'waiting_documents', progress: 0, label: `Waiting for ${documentType}` });
      setTransferred((current) => current.map((item) => (item.id || item.taskId) === task.id ? { ...item, status: 'waiting_documents', label: `Waiting for ${documentType}` } : item));
      toast.success(`${documentType} request sent directly to ${task.assignedByName || 'the sender'}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || error.message || 'Could not request documents');
    }
  };

  const closeMenuThen = (action) => {
    const task = menu?.task;
    setMenu(null);
    if (task) action(task);
  };

  const chooseFileType = (type) => {
    const task = menu?.task;
    setMenu(null);
    if (!task) return;
    setPendingUpload({ task, type });
    setTimeout(() => fileInput.current?.click(), 0);
  };

  const saveConfiguration = async () => {
    const nextConfig = {
      maxRows: Number(draftConfig.maxRows) || 5,
      defaultFilter: draftConfig.defaultFilter || 'new',
    };
    setConfigSaving(true);
    try {
      await updateTenant(tenantId, { quickTasksConfig: nextConfig });
      onConfigChanged?.(nextConfig);
      if (!isVpRisk) setFilter(nextConfig.defaultFilter);
      setConfigOpen(false);
      toast.success('Quick Tasks configuration saved');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not save Quick Tasks configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !pendingUpload) return;
    const { task, type } = pendingUpload;
    setPendingUpload(null);
    try {
      await persistProgress(task, { action: 'Add files', status: 'in_progress', progress: 2, label: `Uploading ${FILE_TYPES.find(([key]) => key === type)?.[1]}…` });
      const signed = type === 'financial'
        ? await getFinancialFileUploadSignedUrl(task.companyId, { fileName: file.name, fileType: file.type, tenantId })
        : await getAdditionalFileUploadSignedUrl(task.companyId, { fileName: file.name, fileType: file.type, tenantId });
      const data = signed?.data || signed;
      if (!data?.uploadUrl) throw new Error('Could not prepare the upload');
      await axios.put(data.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress: ({ loaded, total }) => setProgress(task, { progress: total ? Math.round((loaded / total) * 75) : 35, label: 'Uploading file…' }),
      });
      const metadata = { tenantId, companyId: task.companyId, fileGCSKey: data.fileGCSKey, fileName: file.name, fileSize: file.size, fileType: file.type, documentType: type };
      if (type === 'financial') {
        const record = await createFileRecord(task.companyId, metadata);
        const fileId = record?.data?.id || record?.id;
        setProgress(task, { progress: 85, label: 'Processing financial statements…' });
        if (fileId) await processFile(fileId);
      } else {
        await createAdditionalFile(task.companyId, metadata);
      }
      await persistProgress(task, { status: 'completed', progress: 100, label: 'File added' });
      if (task.taskType === 'document_request' && task.parentTaskId) {
        await updateQuickTask(tenantId, task.parentTaskId, { status: 'pending', progress: 0, label: 'Documents received — ready for approval' });
        await deleteQuickTask(tenantId, task.id);
        setTransferred((current) => current.filter((item) => (item.id || item.taskId) !== task.id));
      }
      toast.success('File added to the counterparty');
    } catch (error) {
      console.error('Quick task upload failed', error);
      await persistProgress(task, { status: 'failed', label: error?.response?.data?.message || error.message || 'Upload failed' });
      toast.error(error?.response?.data?.message || error.message || 'Upload failed');
    }
  };

  if (!tenantId) return null;
  return (
    <Paper
      data-quick-tasks
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: executive ? '18px' : 3,
        overflow: 'hidden',
        borderColor: executive ? '#e4e9f2' : undefined,
        boxShadow: executive ? '0 12px 30px rgba(35,54,91,.06)' : '0 5px 18px rgba(28,46,74,.08)',
      }}
    >
      <Box sx={{ px: 2.5, pt: minimized ? 1.5 : 2.25, pb: minimized ? 1.5 : 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: minimized ? 'center' : 'flex-start', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
              <Typography sx={{ color: executive ? '#17233f' : '#102a43', fontWeight: 700, fontSize: 21 }}>
                {executive ? 'Priority actions' : 'Quick Tasks'}
              </Typography>
              <Tooltip title="A personalized queue based on your role, open work, company risk, and likely next actions." arrow>
                <HelpOutlineIcon sx={{ color: '#718096', fontSize: 18 }} />
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {minimized
                ? `${allRows.length} open ${allRows.length === 1 ? 'task' : 'tasks'} · expand to review`
                : executive
                  ? 'The highest-value decisions and reviews requiring attention now'
                  : 'Newest open work only · completed and closed tasks stay hidden'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!minimized && <Tooltip title="Choose how many open tasks are visible before scrolling and which company group opens by default." arrow>
              <span><Button startIcon={<SettingsIcon />} variant="outlined" size="small" onClick={() => setConfigOpen(true)} disabled={permissions.isViewOnly}>Configure table</Button></span>
            </Tooltip>}
            <Tooltip title={minimized ? 'Expand Quick Tasks' : 'Minimize Quick Tasks'} arrow>
              <IconButton
                aria-label={minimized ? 'Expand Quick Tasks' : 'Minimize Quick Tasks'}
                aria-expanded={!minimized}
                onClick={toggleMinimized}
                size="small"
                sx={{ border: '1px solid #d8e0ec', bgcolor: '#fff' }}
              >
                {minimized ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {!minimized && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {FILTERS.map(([key, label]) => <Tooltip key={key} title={FILTER_HELP[key]} arrow><Chip clickable onClick={() => setFilter(key)} label={`${label} ${count(key)}`} color={filter === key ? 'primary' : 'default'} variant={filter === key ? 'filled' : 'outlined'} /></Tooltip>)}
        </Box>}
      </Box>
      {!minimized && <>
        <input ref={fileInput} hidden type="file" onChange={uploadFile} />
        <TableContainer data-testid="quick-tasks-scroll-container" style={{ maxHeight: tableViewportHeight, overflowY: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
          <TableHead><TableRow sx={{ bgcolor: '#f7f9fc' }}>
            {['Counterparty', 'Why it needs attention', 'Requested limit', 'Recommended limit', 'Approved limit', 'ARC score', 'Status / progress', 'Next action', ''].map((heading, index) => <TableCell key={heading || `finish-${index}`} sx={{ color: '#718096', fontWeight: 700 }}><Tooltip title={HEADER_HELP[heading]} arrow><span>{heading || <CloseIcon fontSize="inherit" />}</span></Tooltip></TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {rows.map((task) => {
              const progress = running[task.id];
              const active = progress?.status === 'in_progress';
              return <TableRow key={task.id} hover>
                <TableCell><Tooltip title="Open this counterparty's complete company report." arrow><Button variant="text" size="small" onClick={() => navigate(companyUrl(task.companyId))} sx={{ p: 0, justifyContent: 'flex-start', fontWeight: 700, textTransform: 'none' }}>{task.companyName}</Button></Tooltip>{task.category === 'transferred' && <Tooltip title="This is the teammate who sent the task; VP Risk decisions and document requests return directly to them." arrow><Typography display="block" variant="caption" color="text.secondary">From {task.assignedByName || 'team member'}</Typography></Tooltip>}</TableCell>
                <TableCell sx={{ maxWidth: 300 }}>{task.reason}</TableCell>
                <TableCell>{money(requestedLimitOf(task.company))}</TableCell>
                <TableCell>{money(recommendedLimitOf(task.company))}</TableCell>
                <TableCell>{money(approvedLimitOf(task.company))}</TableCell>
                <TableCell><Chip size="small" label={score(scoreOf(task.company))} variant="outlined" /></TableCell>
                <TableCell sx={{ width: 235 }}>
                  {progress ? <Box><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}><Typography variant="caption" color={progress.status === 'failed' ? 'error' : 'text.secondary'}>{progress.label}</Typography><Typography variant="caption">{progress.progress || 0}%</Typography></Box><LinearProgress color={progress.status === 'failed' ? 'error' : progress.status === 'completed' ? 'success' : 'primary'} variant="determinate" value={progress.progress || 0} /></Box>
                    : <Chip size="small" label={task.status} color={task.status === 'Ready' ? 'success' : 'default'} variant="outlined" />}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={isVpRisk ? 'Approve, reject, request specific documents, or open the full review.' : 'Upload files, refresh underwriting, export the report, or transfer for approval.'} arrow><span><Button variant="outlined" size="small" endIcon={<KeyboardArrowDownIcon />} disabled={active} onClick={(event) => setMenu({ anchor: event.currentTarget, task, kind: 'actions' })}>{isVpRisk && task.category === 'transferred' ? 'Review decision' : 'Next action'}</Button></span></Tooltip>
                  {active && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell align="right"><Tooltip title="Task finished"><span><IconButton size="small" disabled={active} onClick={() => finishTask(task)}><CloseIcon fontSize="small" /></IconButton></span></Tooltip></TableCell>
              </TableRow>;
            })}
            {!rows.length && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>No open tasks in this view.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </TableContainer>
      </>}
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {menu?.task?.taskType === 'approval' || (isVpRisk && menu?.task?.category === 'transferred') ? <>
          <ExplainedMenuItem explanation="Approve using the current approved, recommended, or requested limit, close this task, and send the decision directly to the teammate who requested it." disabled={!permissions.canApprove} onClick={() => closeMenuThen(approveTask)}>Approve</ExplainedMenuItem>
          <ExplainedMenuItem explanation="Reject the request with a reason and return the decision directly to the teammate who submitted it." disabled={!permissions.canApprove} onClick={() => { const task = menu?.task; setMenu(null); setRejectDialog({ open: true, task, reason: '' }); }}>Reject</ExplainedMenuItem>
          {FILE_TYPES.map(([, label]) => <ExplainedMenuItem key={label} explanation={`Ask the original sender to upload ${label.toLowerCase()}; the request appears immediately in their transferred tasks.`} disabled={permissions.isViewOnly || !permissions.canApprove} onClick={() => requestDocuments(label)}>Request: {label}</ExplainedMenuItem>)}
          <ExplainedMenuItem explanation="Download the latest company underwriting report as a PDF." disabled={!permissions.canDownloadReports} onClick={() => closeMenuThen((task) => runAction(task, 'export'))}>Export report</ExplainedMenuItem>
          <ExplainedMenuItem explanation="Open the complete company report without closing this task." onClick={() => closeMenuThen((task) => navigate(companyUrl(task.companyId)))}>Open company report</ExplainedMenuItem>
        </> : <>
          {FILE_TYPES.map(([key, label]) => <ExplainedMenuItem key={key} explanation={`Upload ${label.toLowerCase()} directly to this counterparty and track processing progress in the row.`} disabled={!permissions.canUploadDocuments} onClick={() => chooseFileType(key)}>Add: {label}</ExplainedMenuItem>)}
          <ExplainedMenuItem explanation="Run underwriting again using the latest company data and watch progress until it finishes." disabled={permissions.isViewOnly} onClick={() => closeMenuThen((task) => runAction(task, 'refresh'))}>Refresh underwriting</ExplainedMenuItem>
          <ExplainedMenuItem explanation="Download the latest available company report now." disabled={!permissions.canDownloadReports} onClick={() => closeMenuThen((task) => runAction(task, 'export'))}>Export report</ExplainedMenuItem>
          <ExplainedMenuItem explanation="Refresh underwriting first, then automatically download the updated report when processing finishes." disabled={permissions.isViewOnly || !permissions.canDownloadReports} onClick={() => closeMenuThen((task) => runAction(task, 'exportAfter'))}>Export after refresh</ExplainedMenuItem>
          {menu?.task?.category !== 'transferred' && <ExplainedMenuItem explanation="Assign this company to VP Risk for approval, rejection, or a request for more documents." disabled={permissions.isViewOnly} onClick={() => closeMenuThen(transferForApproval)}>Send to VP Risk</ExplainedMenuItem>}
          <ExplainedMenuItem explanation="Open the complete company report without closing this task." onClick={() => closeMenuThen((task) => navigate(companyUrl(task.companyId)))}>Open company report</ExplainedMenuItem>
        </>}
      </Menu>
      <Dialog open={introOpen} onClose={closeIntro} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 3.5, pb: 2.5, bgcolor: '#f3f7ff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ color: '#102a43', fontWeight: 800 }}>Meet your personal Quick Tasks</Typography>
            <Chip label="SHOWN ONCE" size="small" sx={{ bgcolor: '#e2edff', color: '#2455b8', fontWeight: 800 }} />
          </Box>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Built around the actions users perform most often in CrediArc—then personalized to your role, your open work, and your portfolio.
          </Typography>
        </Box>
        <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
          <Box sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f0f6ff', border: '1px solid #c8dcff' }}>
            <Typography sx={{ color: '#183b70', fontWeight: 800 }}>Your queue is private and personal</Typography>
            <Typography variant="body2" color="text.secondary">Each user sees their own tasks, suggested actions, and transferred work.</Typography>
          </Box>
          <Typography sx={{ color: '#102a43', fontWeight: 800, fontSize: 18, mb: 1.5 }}>How it works</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <Box sx={{ p: 2, minHeight: 190, borderRadius: 2, bgcolor: '#fbfcfe', border: '1px solid #dbe3ee' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip label="1" color="primary" sx={{ fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800 }}>Choose a view</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75, mb: 2 }}>
                <Chip size="small" label="New 4" color="primary" />
                <Chip size="small" label="AI 6" variant="outlined" />
                <Chip size="small" label="Risk 2" variant="outlined" />
                <Chip size="small" label="Sent 1" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary">Filter new, AI-recommended, high-risk, or transferred work.</Typography>
            </Box>
            <Box sx={{ p: 2, minHeight: 190, borderRadius: 2, bgcolor: '#fbfcfe', border: '1px solid #dbe3ee' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip label="2" color="primary" sx={{ fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800 }}>Take action</Typography>
              </Box>
              <Button fullWidth variant="outlined" endIcon={<KeyboardArrowDownIcon />} sx={{ justifyContent: 'space-between', mb: 1, pointerEvents: 'none' }}>Next action</Button>
              <Paper variant="outlined" sx={{ px: 1.25, py: .75, mb: 2, fontSize: 13, color: '#44566c' }}>Refresh underwriting</Paper>
              <Typography variant="body2" color="text.secondary">Use the suggested shortcut or open the full company report.</Typography>
            </Box>
            <Box sx={{ p: 2, minHeight: 190, borderRadius: 2, bgcolor: '#fbfcfe', border: '1px solid #dbe3ee' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip label="3" color="primary" sx={{ fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800 }}>Track and finish</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Refreshing underwriting…</Typography>
              <LinearProgress variant="determinate" value={68} sx={{ my: 1 }} />
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>✓ Underwriting refreshed</Typography>
              <Typography variant="body2" color="text.secondary">Follow progress in the row, then close the task when it is done.</Typography>
            </Box>
          </Box>
          {isVpRisk && (
            <Typography variant="body2" sx={{ mt: 2, color: '#52667a' }}>
              VP Risk requests open in the transferred view, where you can approve, reject, or request documents and return the result to the sender.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Tip: hover over filters, columns, and actions for an explanation at any time.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2.25, borderTop: '1px solid #e3e9f0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>This introduction will not appear again after you continue.</Typography>
          <Button variant="contained" onClick={closeIntro}>Show my Quick Tasks</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, task: null, reason: '' })} fullWidth maxWidth="xs">
        <DialogTitle>Reject {rejectDialog.task?.companyName || 'approval request'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>The reason will be transferred directly to {rejectDialog.task?.assignedByName || 'the teammate who submitted the request'}.</Typography>
          <TextField autoFocus fullWidth multiline minRows={3} label="Reason for rejection" value={rejectDialog.reason} onChange={(event) => setRejectDialog((current) => ({ ...current, reason: event.target.value }))} placeholder="Explain what must change before this can be approved" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, task: null, reason: '' })}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!rejectDialog.reason.trim()} onClick={rejectTask}>Reject and return</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={configOpen} onClose={() => !configSaving && setConfigOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Configure Quick Tasks table</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">Controls apply to everyone in this client account. The table always shows the newest open tasks first, with additional open tasks available by scrolling.</Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="quick-task-row-limit-label">Visible tasks</InputLabel>
            <Select labelId="quick-task-row-limit-label" label="Visible tasks" value={draftConfig.maxRows} onChange={(event) => setDraftConfig((current) => ({ ...current, maxRows: event.target.value }))}>
              {[5, 10, 15, 20].map((value) => <MenuItem key={value} value={value}>{value} rows before scrolling</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="quick-task-default-view-label">Default view</InputLabel>
            <Select labelId="quick-task-default-view-label" label="Default view" value={draftConfig.defaultFilter} onChange={(event) => setDraftConfig((current) => ({ ...current, defaultFilter: event.target.value }))}>
              {FILTERS.filter(([key]) => key !== 'transferred').map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)} disabled={configSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveConfiguration} disabled={configSaving}>{configSaving ? 'Saving…' : 'Save configuration'}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
